import { Subscription, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import type { IValidateLoginCodeRequestData, IValidatePasswordResetCodeRequestData } from 'src/app/interfaces/authentication.interface';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { GlobalErrorHandlerService } from 'src/app/services/global-errors/global-error-handler.service';
import { commonTags, emailTags, mfaLoginStepTags, noMFALoginTags, phoneTags } from './text-tags';
import { Platform } from '@ionic/angular';
import { Navigator } from '../menu/navigator';

@Component({
  selector: 'app-validate-pin',
  templateUrl: './validate-pin.component.html',
  styleUrls: ['./validate-pin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ValidatePinComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  @ViewChild('verifiedCodeOkBtn', { read: ElementRef })
  verifiedCodeOkBtn: ElementRef<HTMLElement>;

  pinVerifyForm: FormGroup;
  pinNumber: AbstractControl<string>;
  disableResend = false;
  tooManyCodeAttemps = false;
  verifiedCode = false;
  textTags: { [key: string]: string };

  @Input() currentDisplayChannel = 'SMS';
  @Input() channels: string[] = ['SMS', 'EMAIL'];
  @Input() phoneNumber: string;
  @Input() email: string;
  @Input() isUpdatePhone = false;
  @Input() isMFALoginStep = true;
  @Input() customerId: number;
  @Input() isForgetPassword: boolean = false;
  @Input() isForgetPasswordToken: string = null;
  @Input() autoValidation: boolean = false;

  @Output() updatePhoneNumber = new EventEmitter<string>(true);
  @Output() validatePinError = new EventEmitter<GlobalErrorHandlerService>(true);
  @Output() validatePinSuccess = new EventEmitter<boolean>(true);
  @Output() pinVerifiedUserAction = new EventEmitter<boolean>(true);
  @Output() validatePin = new EventEmitter<string>(true);
  
  @ViewChild('inputPin') myInput;

  private error: GlobalErrorHandlerService | null = null;
  private verifiedCodeOkBtnFocusTimer: number;
  private disableResendLinkTimer: number;
  private pinNumberValueChangesSub: Subscription;
  private currentDisplayChannelSub: Subscription;
  private disableResendLinkTime: number;
  private disableResendClickCounter = 0;
  public currentDisplayChannel$: BehaviorSubject<string>;
  public isMobile: boolean = false;
  private inputLength = 6;
  public isMobileOrWeb: boolean = false;
  bypassPinSuccessUI: any = false;

  constructor(
    private configService: ConfigService,
    private authenticationService: AuthenticationService,
    private globalErrorHandlerService: GlobalErrorHandlerService,
    private platform: Platform,
    private cgd: ChangeDetectorRef,
    private navigator:Navigator
  ) {
    this.textTags = { ...commonTags };
    this.detectMobile();
    this.changeInputForMobile();
  }

  ngOnInit(): void {
    if (!this.phoneNumber) {
      this.channels = ['EMAIL'];
      if (this.currentDisplayChannel === 'SMS') {
        this.currentDisplayChannel = 'EMAIL';
      }
    }

    this.currentDisplayChannel$ = new BehaviorSubject<string>(this.currentDisplayChannel);

    if (this.isMFALoginStep) {
      this.textTags = { ...this.textTags, ...mfaLoginStepTags };
    }
    else {
      this.textTags = { ...this.textTags, ...noMFALoginTags };
    }

    this.currentDisplayChannelSub = this.currentDisplayChannel$
      .pipe(distinctUntilChanged())
      .subscribe(channel => {
        if (channel === 'SMS') {
          this.textTags = { ...this.textTags, ...phoneTags }
        }
        else {
          this.textTags = { ...this.textTags, ...emailTags }
        }
      })

    this.pinVerifyForm = new FormGroup({
      pinNumber: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
        Validators.minLength(this.inputLength),
        Validators.maxLength(this.inputLength),
      ]),
    });

    this.pinNumber = this.pinVerifyForm.get('pinNumber');

    //set the config value 20 seconds less so, don't have to make the timeout logic complicated.
    const mfaResendLinkTimeout = this.configService.getConfig().FEATURE_MFA_RESEND_LINK_TIMEOUT
    this.disableResendLinkTime = mfaResendLinkTimeout ?? 20;

    // setting maxinput length from config service by default set to 6
    this.inputLength = this.configService.getConfig().FEATURE_SMS_PIN_LENGTH ?? 6;

    const autoValidationConfig = this.configService.getConfig().FEATURE_LOGIN_MFA_AUTOVERIFICATION === '1';
    if (this.autoValidation && autoValidationConfig) {
      this.pinVerifyForm.valueChanges.pipe(
        filter(() => this.pinVerifyForm.valid)
      ).subscribe(() => {
        this.doPINVerify();
      })
    }
  }

  protected detectMobile() {
    if (this.platform.is('capacitor')) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
    this.isMobileOrWeb = this.platform.is("capacitor") || this.platform.is('mobileweb');
  }

  ngAfterViewInit(): void {
    // reset the existed error when uesr has input
    this.pinNumberValueChangesSub = this.pinNumber.valueChanges
      .pipe(filter(() => !!this.error))
      .subscribe(() => {
        this.error = null,
          this.sendValidatePinError(null);
      });
  }

  ngAfterViewChecked(): void {
    if (this.verifiedCode && this.verifiedCodeOkBtn && !this.verifiedCodeOkBtnFocusTimer) {
      // try to enable 'press enter key' works for verifiedCodeOkBtnEle
      const verifiedCodeOkBtnEle = this.verifiedCodeOkBtn.nativeElement;
      if (verifiedCodeOkBtnEle) {
        this.verifiedCodeOkBtnFocusTimer = window.setTimeout(() => {
          verifiedCodeOkBtnEle.focus();
        }, 100);
      }
    }
  }

  changeInputForMobile() {
    if (this.isMobileOrWeb) {
      setTimeout(() => {
        if (this.myInput) {
          this.myInput.setFocus();
          this.cgd.detectChanges();
        }
      }, 500);
    }
  }

  ngOnDestroy(): void {
    if (this.verifiedCodeOkBtnFocusTimer) {
      clearTimeout(this.verifiedCodeOkBtnFocusTimer);
    }
    if (this.disableResendLinkTimer) {
      clearTimeout(this.disableResendLinkTimer);
    }
    if (this.pinNumberValueChangesSub) {
      this.pinNumberValueChangesSub.unsubscribe();
    }
    if (this.currentDisplayChannelSub) {
      this.currentDisplayChannelSub.unsubscribe();
    }
  }

  private handlePINVerifyErrorCode(code: string, error: GlobalErrorHandlerService) {
    switch (code) {
      case 'LOGIN_CODE_NOT_FOUND':
        error.setErrorMessage(this.textTags['LOGIN_CODE_NOT_FOUND']);
        this.pinNumber.setErrors({ LOGIN_CODE_NOT_FOUND: true });
        this.pinNumber.markAsDirty();
        this.pinNumber.markAsTouched();
        break;
      case 'LOGIN_CODE_EXPIRED':
        error.setErrorMessage(this.textTags['LOGIN_CODE_EXPIRED']);
        this.pinNumber.setErrors({ LOGIN_CODE_EXPIRED: true });
        this.pinNumber.markAsDirty();
        this.pinNumber.markAsTouched();
        break;
      case 'FAILED_TOO_MANY_ATTEMPTS':
        error.setErrorMessage(this.textTags['FAILED_TOO_MANY_ATTEMPTS']);
        this.tooManyCodeAttemps = true;
        break;
      case "NOT_VALID_TOKEN":
        this.pinNumber.setErrors({ LOGIN_CODE_NOT_FOUND: true });
        this.pinNumber.markAsDirty();
        this.pinNumber.markAsTouched();
        error.setErrorMessage('The code provided is not valid');
        break;
      default:
        error.setErrorMessage(this.textTags['default_pin_verify_error']);
        this.pinNumber.setErrors({ LOGIN_CODE_SERVER_ERROR: true });
        this.pinNumber.markAsDirty();
        this.pinNumber.markAsTouched();
        break;
    }
    this.verifiedCode = false;
    this.error = error;
    this.sendValidatePinError(error);
  }

  async doPINVerify() {
    if (this.pinVerifyForm.invalid) {
      return;
    }
    if (this.verifiedCode) {
      return;
    }
    if (!this.isForgetPassword) {
      const data: IValidateLoginCodeRequestData = {
        customerId: this.customerId,
        pinNumber: this.pinNumber.value,
      }
      this.authenticationService.validateLoginCode(data as IValidateLoginCodeRequestData).subscribe(
        (response) => {
          if ('code' in response) {
            const error = this.globalErrorHandlerService;
            this.handlePINVerifyErrorCode(response.code, error);
          } else {
            this.pinVerified();
          }
        },
        (error) => {
          this.handlePINVerifyErrorCode(error.error_code, this.globalErrorHandlerService);
        }
      );
    } else if(this.isForgetPassword && this.email) {
      // this.validatePin.emit(this.pinNumber.value);
      this.onValidateToken(this.pinNumber.value);
    }
    else {
      const data: IValidatePasswordResetCodeRequestData = {
        customerId: this.customerId,
        pinNumber: this.pinNumber.value,
        resetPasswordToken: this.isForgetPasswordToken
      }
      this.authenticationService.validatePasswordResetCode(data as IValidatePasswordResetCodeRequestData).subscribe(
        (response) => {
          if ('code' in response) {
            const error = this.globalErrorHandlerService;
            this.handlePINVerifyErrorCode(response.code, error);
          } else {
            this.pinVerified();
          }
        },
        (error) => {
          this.handlePINVerifyErrorCode(error.error_code, error);
        });
    }
  }

  resendPin(channel?: string) {
    const channelParam = channel ?? (this.currentDisplayChannel$.value);
    this.error = null;
    this.sendValidatePinError(this.error);
    this.authenticationService.sendLoginCode(this.customerId, channelParam,
      this.isUpdatePhone ? this.phoneNumber : null).subscribe((response) => {
        if (response.result == 1) {
          this.disableResendLink();
          this.currentDisplayChannel$.next(channelParam);
        }
      },
        (error) => {
          error.setErrorMessage(this.textTags['send_code_failed']);
          this.error = error;
          this.sendValidatePinError(error);
        }
      );
    return false;
  }
  onValidateToken(token){
    this.authenticationService.callValidateTokenMFAEmail({token: token, email: this.email }).subscribe(
        (response: any) => {
            if (response && !!response.result) {
                this.error = null;
                // this.content.scrollToTop();
                this.customerId = response.payload.customer_id;
                this.cgd.detectChanges();
                this.navigator.navigateTo("ResetPasswordMFASecondStepComponent",{},{"token": token, "customerId": this.customerId, "email": this.email});
            }
        },
        (error) => {
          
            switch (error.getErrorCode().toUpperCase()) {
                case 'INVALID_TOKEN':
                    error.setErrorMessage('The token provided is invalid');
                    break;
                case "TOKEN_EXPIRED":
                    error.setErrorMessage('The token provided is expired');
                    break;
                case "NOT_VALID_TOKEN":
                    error.setErrorMessage('The token provided is expired');
                    break;
                default:
                  error.setErrorMessage('Something went wrong.');
                  break;

            }
            this.error = error;
            this.handlePINVerifyErrorCode(error.error_code, error);
            // this.tokenExpired = true;
            // this.content.scrollToTop(10);
            this.cgd.detectChanges();
        }
    );
}


  disableResendLink() {
    this.disableResend = true;
    this.disableResendClickCounter++;
    // 'Code has been resent. If you still are not receiving your code, please contact support.';
    const timeout =
      this.disableResendLinkTime * 1000 +
      this.disableResendClickCounter * 10000;
    this.disableResendLinkTimer = window.setTimeout(() => {
      this.disableResend = false;
      this.cgd.detectChanges();
    }, timeout);
  }

  sendValidatePinError(error) {
    this.validatePinError.emit(error);
  }

  userActionAfterCodeVerified() {
    this.pinVerifiedUserAction.emit(true)
  }

  pinVerified() {
    this.verifiedCode = true;
    this.error = null;
    this.sendValidatePinError(this.error);
    this.validatePinSuccess.emit(true);
    this.userActionAfterCodeVerified();
  }

  onSubmit() {
    this.doPINVerify();
  }

  resendPinForgetPassword() {
    this.error = null;
    this.sendValidatePinError(this.error);
    const channelParam = "EMAIL" ?? (this.currentDisplayChannel$.value);
    this.authenticationService.callForgotPassword({email: this.email}).subscribe(()=>
    {
      this.disableResendLink();
      this.currentDisplayChannel$.next(channelParam);
    }
    );
    
  }

  resendPinForgetPasswordSMS() {
    this.error = null;
    this.sendValidatePinError(this.error);
    const channelParam = "SMS" ?? (this.currentDisplayChannel$.value);
    this.authenticationService.sendForgotPasswordLoginCode(this.customerId).subscribe(()=>
    {
      this.disableResendLink();
      this.currentDisplayChannel$.next(channelParam);
    }
    );
    
  }
}
